<script>
import Multiselect from 'vue-multiselect'
import { debounce } from '@/mixin/helper'
import { getVSMSRestClient } from '@/services/vsms'
import { getSmsClient } from '@/services/sms'
import * as sensorTypeApi from '@/services/rest-api-tools/lib/sensor-type/sensorType'
import * as customProfileApi from '@/services/rest-api-sms/lib/custom-profile/custom-profile'

const vsmsAxios = getVSMSRestClient()
const smsAxios = getSmsClient()

export default {
  components: {
    Multiselect,
  },
  data() {
    return {
      assembleForm: this.getDefaultForm(),
      vehicleStatuses: [],
      vehicleStatusPagination: {
        page: 0,
        limit: 10,
        totalPage: null,
      },
      vehicleStatusSearch: '',
      isLoadingVehicleStatuses: false,
      sensorTypes: [],
      isLoadingSensorTypes: false,
      customProfiles: [],
      isLoadingCustomProfiles: false,
      isSubmitting: false,
    }
  },
  computed: {
    selectedDeviceCode() {
      return this.assembleForm.vehicleStatus?.vehicle?.device?.device_code || null
    },
    hasNextVehicleStatusPage() {
      if (this.vehicleStatusPagination.totalPage === null) return true
      return this.vehicleStatusPagination.page < this.vehicleStatusPagination.totalPage
    },
    canSubmit() {
      return Boolean(this.assembleForm.vehicleStatus && this.assembleForm.custom_profile && !this.isSubmitting)
    },
  },
  watch: {
    selectedDeviceCode() {
      this.assembleForm.custom_profile = null
      this.customProfiles = []
      this.getCustomProfiles()
    },
  },
  created() {
    this.getVehicleStatuses({ reset: true })
    this.getSensorTypes()
  },
  methods: {
    getDefaultForm() {
      return {
        vehicleStatus: null,
        sensors: [{ sensor: null }],
        custom_profile: null,
      }
    },
    async assemblePackage() {
      if (!this.canSubmit) return

      this.isSubmitting = true

      try {
        const payload = this.buildPackagePayload()
        await smsAxios.post('/package', payload)

        this.$mcToast.success(`Package ...${payload.imei} assembled`)
        this.assembleForm = this.getDefaultForm()
        this.$emit('assembled')
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSubmitting = false
      }
    },
    buildPackagePayload() {
      return {
        imei: this.assembleForm.vehicleStatus?.vehicle?.imei,
        sensor_code: this.assembleForm.sensors
          .map((item) => item.sensor?.sensor_code)
          .filter((sensorCode) => sensorCode !== undefined && sensorCode !== null),
        custom_profile_id: this.assembleForm.custom_profile?.custom_profile_id,
      }
    },
    async getVehicleStatuses({ reset = false } = {}) {
      if (this.isLoadingVehicleStatuses) return
      if (!reset && !this.hasNextVehicleStatusPage) return

      this.isLoadingVehicleStatuses = true

      const params = {
        page: reset ? 1 : this.vehicleStatusPagination.page + 1,
        limit: this.vehicleStatusPagination.limit,
        search: this.vehicleStatusSearch || undefined,
        sorts: 'vehicle_id desc',
        filters: [],
      }

      try {
        const response = await vsmsAxios.get('/vehicle-status', { params })
        const responseData = response.data || {}
        const data = responseData.data || {}
        const metadata = responseData.metadata || data.metadata || {}
        const paginatedResult = data.paginated_result || []

        this.vehicleStatuses = reset
          ? paginatedResult
          : [...this.vehicleStatuses, ...paginatedResult]
        this.vehicleStatusPagination.page = metadata.page || params.page
        this.vehicleStatusPagination.totalPage = metadata.total_page || params.page
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoadingVehicleStatuses = false
      }
    },
    handleVehicleStatusSearch: debounce(function (search) {
      this.vehicleStatusSearch = search
      this.getVehicleStatuses({ reset: true })
    }, 500),
    handleVehicleStatusObserved(isVisible) {
      if (!isVisible) return
      this.getVehicleStatuses()
    },
    async getSensorTypes() {
      this.isLoadingSensorTypes = true

      try {
        const response = await sensorTypeApi.get({
          page: 1,
          limit: 100000,
        })
        this.sensorTypes = response || []
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoadingSensorTypes = false
      }
    },
    async getCustomProfiles() {
      if (!this.selectedDeviceCode) return

      this.isLoadingCustomProfiles = true

      try {
        const response = await customProfileApi.get({
          page: 1,
          limit: 10,
          filters: [`device_code eq ${this.selectedDeviceCode}`],
        })
        this.customProfiles = response?.data?.paginated_result || []
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoadingCustomProfiles = false
      }
    },
    addSensorField() {
      this.assembleForm.sensors.push({ sensor: null })
    },
    removeSensorField(index) {
      if (this.assembleForm.sensors.length === 1) {
        this.assembleForm.sensors = [{ sensor: null }]
        return
      }

      this.assembleForm.sensors.splice(index, 1)
    },
    vehicleStatusLabel(item) {
      if (!item) return ''

      const imei = item.vehicle?.imei || item.license_plate || '-'
      const deviceName = item.vehicle?.device?.device_name || '-'
      const deviceCode = item.vehicle?.device?.device_code || '-'
      return `${imei} - ${deviceName} (${deviceCode})`
    },
    sensorTypeLabel(item) {
      if (!item) return ''

      return `${item.sensor_name} (${item.sensor_code})`
    },
    customProfileLabel(item) {
      if (!item) return ''

      return `${item.name} (${item.code})`
    },
    handleError(error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong'

      if (this.$toast) {
        this.$toast.error(message)
        return
      }

      this.$mcToast?.error(message)
    },
  },
}
</script>

<template>
  <section class="package-planner__panel">
    <h2>Assemble a Package</h2>
    <p>
      Unpackaged GPS IMEIs come from the ERP and are already QC checked, so an assembled Package starts as QC Passed.
      A FMB-125 can take a Fuel sensor, a FMB-130 can take a Dashcam, and both can take a BLE Sensor.
      7 IMEIs waiting. Parts in stock: Sby - Fuel sensor 4, Dashcam 2, BLE Sensor 3 | Jkt - Fuel sensor 3,
      Dashcam 0, BLE Sensor 2.
    </p>

    <b-form @submit.prevent="assemblePackage">
      <b-row>
        <b-col lg="4" md="6" class="mb-3">
          <label class="package-planner__label" for="package-imei">GPS IMEI (not packaged yet)</label>
          <multiselect
            id="package-imei"
            v-model="assembleForm.vehicleStatus"
            :options="vehicleStatuses"
            :custom-label="vehicleStatusLabel"
            :internal-search="false"
            :loading="isLoadingVehicleStatuses"
            :option-height="44"
            :max-height="220"
            track-by="vehicle_id"
            placeholder="Choose an IMEI"
            @open="getVehicleStatuses({ reset: true })"
            @search-change="handleVehicleStatusSearch"
          >
            <template #afterList>
              <div v-if="isLoadingVehicleStatuses" class="package-planner__select-state">
                Loading...
              </div>
              <div
                v-if="hasNextVehicleStatusPage"
                v-observe-visibility="handleVehicleStatusObserved"
                class="package-planner__select-sentinel"
              ></div>
            </template>
            <template #noResult>No IMEI found.</template>
            <template #noOptions>Type to search IMEI.</template>
          </multiselect>
        </b-col>

        <b-col lg="4" md="6" class="mb-3">
          <label class="package-planner__label" for="package-custom-profile">Custom Profile</label>
          <multiselect
            id="package-custom-profile"
            v-model="assembleForm.custom_profile"
            :options="customProfiles"
            :custom-label="customProfileLabel"
            :loading="isLoadingCustomProfiles"
            :disabled="!selectedDeviceCode"
            track-by="custom_profile_id"
            placeholder="Choose custom profile"
          >
            <template #noOptions>
              {{ selectedDeviceCode ? 'No custom profile found.' : 'Choose GPS IMEI first.' }}
            </template>
          </multiselect>
        </b-col>
      </b-row>

      <div class="package-planner__sensor-list">
        <b-row
          v-for="(sensorField, index) in assembleForm.sensors"
          :key="index"
          class="align-items-end"
        >
          <b-col lg="4" md="6" class="mb-3">
            <label class="package-planner__label" :for="`package-sensor-${index}`">Sensor</label>
            <multiselect
              :id="`package-sensor-${index}`"
              v-model="sensorField.sensor"
              :options="sensorTypes"
              :custom-label="sensorTypeLabel"
              :loading="isLoadingSensorTypes"
              track-by="sensor_code"
              placeholder="Choose sensor"
            >
              <template #noOptions>No sensor found.</template>
            </multiselect>
          </b-col>

          <b-col md="3" class="mb-3">
            <b-button
              variant="outline-danger"
              type="button"
              class="package-planner__sensor-remove"
              @click="removeSensorField(index)"
            >
              <i class="bx bx-trash-alt"></i>
              Remove
            </b-button>
          </b-col>
        </b-row>

        <b-button
          variant="outline-primary"
          type="button"
          class="package-planner__sensor-add"
          @click="addSensorField"
        >
          <i class="bx bx-plus"></i>
          Add Sensor
        </b-button>
      </div>

      <b-button
        variant="primary"
        type="submit"
        :disabled="!canSubmit"
      >
        <b-spinner v-if="isSubmitting" small />
        {{ isSubmitting ? 'Loading...' : 'Assemble Package' }}
      </b-button>
    </b-form>
  </section>
</template>

<style scoped>
.package-planner__panel {
  padding: 24px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
}

.package-planner__panel h2 {
  margin: 0 0 14px;
  color: #111827;
  font-size: 18px;
  font-weight: 700;
}

.package-planner__panel p {
  color: #667085;
  font-size: 14px;
  line-height: 1.6;
}

.package-planner__label {
  display: block;
  margin-bottom: 8px;
  color: #667085;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .02em;
  text-transform: uppercase;
}

.package-planner__sensor-list {
  margin-bottom: 18px;
}

.package-planner__sensor-add,
.package-planner__sensor-remove {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.package-planner__select-state {
  padding: 10px 12px;
  color: #667085;
  font-size: 13px;
}

.package-planner__select-sentinel {
  height: 12px;
}

::v-deep .custom-select,
::v-deep .form-control,
::v-deep .multiselect__tags {
  min-height: 44px;
  border-color: #d0d5dd;
  border-radius: 8px;
  color: #344054;
  background-color: #fff;
}

::v-deep .multiselect__placeholder,
::v-deep .multiselect__single,
::v-deep .multiselect__input {
  color: #344054;
  font-size: 14px;
}

::v-deep .multiselect__placeholder {
  color: #98a2b3;
}

::v-deep .multiselect__option--highlight {
  background: #556ee6;
}

@media (max-width: 767.98px) {
  .package-planner__panel {
    padding: 18px;
  }
}
</style>
